import { FC, useState, useEffect } from 'react';
import {
    Avatar,
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Chip,
    LinearProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    Add as AddIcon,
    Image as ImageIcon
} from '@mui/icons-material';
import { adminApiClient } from '../utils/adminApiClient';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { IClient, ICreateClientDto } from "@tintoa/identity-service-client";

interface Client extends IClient {
    icon?: { data: string; contentType: string; uploadedAt: Date | string };
}


export const ClientManagement: FC = () => {
    const [clients, setClients] = useState<Client[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingClient, setEditingClient] = useState<Client | null>(null);
    const [availableRoles, setAvailableRoles] = useState<Array<{id: string; name: string; ident: string}>>([]);
    const [formData, setFormData] = useState<ICreateClientDto>({
        name: '',
        description: '',
        authCallbackUrl: '',
        url: '',
        requiredRoleIds: [],
        isPrivate: false
    });
    const [errors, setErrors] = useState<Partial<ICreateClientDto>>({});
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [removeIcon, setRemoveIcon] = useState(false);
    const [iconError, setIconError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    useEffect(() => {
        loadClients();
    }, []);

    const loadAvailableRoles = async () => {
        try {
            const roles = await adminApiClient.getRoles();
            setAvailableRoles(roles);
        } catch (error) {
            console.error('Error loading available roles:', error);
        }
    };

    const loadClients = async () => {
        try {
            setLoading(true);
            
            // First test the API connection
            try {
                await adminApiClient.testApiConnection();
            } catch (testError: any) {
                console.error('API connection test failed:', testError);
                enqueueSnackbar(`API-Verbindungstest fehlgeschlagen: ${testError.message}`, { variant: 'error' });
                return;
            }
            
            const data = await adminApiClient.getClients();
            // Transform IClient[] to Client[] by ensuring apiKey values are strings and secret is included
            const transformedData: Client[] = data.map(client => ({
                ...client,
                secret: client.secret || '',
                apiKeys: client.apiKeys.map(apiKey => ({
                    name: apiKey.name,
                    value: apiKey.value || ''
                })),
                requiredRoleIds: client.requiredRoleIds ? client.requiredRoleIds.map((id: any) => id.toString()) : []
            }));
            setClients(transformedData);
        } catch (error: any) {
            console.error('Failed to load clients:', error);
            
            // Check if it's a JSON parsing error (HTML response)
            if (error instanceof SyntaxError && error.message.includes('Unexpected token')) {
                enqueueSnackbar('Server-Fehler: Ungültige Antwort erhalten. Bitte überprüfen Sie die Authentifizierung.', { variant: 'error' });
            } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
                enqueueSnackbar('Authentifizierungsfehler: Bitte melden Sie sich erneut an.', { variant: 'error' });
            } else if (error.message?.includes('403') || error.message?.includes('Forbidden')) {
                enqueueSnackbar('Zugriff verweigert: Sie haben keine Admin-Berechtigung.', { variant: 'error' });
            } else {
                enqueueSnackbar(`Fehler beim Laden der Clients: ${error.message || 'Unbekannter Fehler'}`, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (client?: Client) => {
        if (client) {
            setEditingClient(client);
            setFormData({
                name: client.name,
                description: client.description,
                authCallbackUrl: client.authCallbackUrl || '',
                url: client.url || '',
                requiredRoleIds: client.requiredRoleIds,
                isPrivate: client.isPrivate ?? false
            });
        } else {
            setEditingClient(null);
            setFormData({
                name: '',
                description: '',
                authCallbackUrl: '',
                url: '',
                requiredRoleIds: [],
                isPrivate: false
            });
        }
        setErrors({});
        setIconFile(null);
        setRemoveIcon(false);
        setIconError(null);
        setOpenDialog(true);
        loadAvailableRoles();
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingClient(null);
        setFormData({
            name: '',
            description: '',
            authCallbackUrl: '',
            url: '',
            requiredRoleIds: [],
            isPrivate: false
        });
        setErrors({});
        setIconFile(null);
        setRemoveIcon(false);
        setIconError(null);
    };

    const validateIconFile = (file: File): boolean => {
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setIconError('Nur JPEG, PNG oder SVG erlaubt.');
            return false;
        }
        if (file.size > 100 * 1024) {
            setIconError('Datei darf maximal 100KB groß sein.');
            return false;
        }
        setIconError(null);
        return true;
    };

    const handleIconFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (validateIconFile(file)) {
            setIconFile(file);
            setRemoveIcon(false);
        }
    };

    const extractFQDN = (url: string): string => {
        try {
            const urlObj = new URL(url);
            return `${urlObj.protocol}//${urlObj.host}`;
        } catch {
            return '';
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<ICreateClientDto> = {};
        
        if (!formData.name.trim()) {
            newErrors.name = 'Name ist erforderlich';
        }

        const authCallbackUrl = formData.authCallbackUrl?.trim() ?? '';
        if (authCallbackUrl) {
            try {
                new URL(authCallbackUrl);
            } catch {
                newErrors.authCallbackUrl = 'Auth Callback URL ist ungültig';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        if (iconFile && !validateIconFile(iconFile)) return;

        setSubmitting(true);
        try {
            const authCallbackUrl = formData.authCallbackUrl?.trim() ?? '';
            const submitData: ICreateClientDto = {
                name: formData.name,
                authCallbackUrl,
                requiredRoleIds: formData.requiredRoleIds || []
            };
            if (formData.description && formData.description.trim()) {
                submitData.description = formData.description;
            }
            if (formData.url && formData.url.trim()) {
                submitData.url = formData.url;
            }
            if (formData.isPrivate !== undefined) {
                submitData.isPrivate = formData.isPrivate;
            }

            let clientId: string;

            if (editingClient) {
                await adminApiClient.updateClient(editingClient.id, submitData);
                clientId = editingClient.id;
                enqueueSnackbar('Client erfolgreich aktualisiert', { variant: 'success' });
            } else {
                const created = await adminApiClient.createClient(submitData);
                clientId = created.id;
                enqueueSnackbar('Client erfolgreich erstellt', { variant: 'success' });
            }

            if (iconFile) {
                try {
                    await adminApiClient.uploadClientIcon(clientId, iconFile);
                    enqueueSnackbar('Icon erfolgreich hochgeladen', { variant: 'success' });
                } catch (iconErr) {
                    console.error('Failed to upload icon:', iconErr);
                    enqueueSnackbar('Fehler beim Hochladen des Icons', { variant: 'error' });
                }
            } else if (editingClient && removeIcon && editingClient.icon) {
                try {
                    await adminApiClient.deleteClientIcon(clientId);
                    enqueueSnackbar('Icon entfernt', { variant: 'success' });
                } catch (iconErr) {
                    console.error('Failed to delete icon:', iconErr);
                    enqueueSnackbar('Fehler beim Entfernen des Icons', { variant: 'error' });
                }
            }

            handleCloseDialog();
            loadClients();
        } catch (error) {
            console.error('Failed to save client:', error);
            enqueueSnackbar('Fehler beim Speichern des Clients', { variant: 'error' });
        } finally {
            setSubmitting(false);
        }
    };



    if (loading) {
        return (
            <Box sx={{ width: '100%' }}>
                <LinearProgress />
            </Box>
        );
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Client Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Verwalten Sie Client-Anwendungen und deren Konfiguration
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Neuer Client
                </Button>
            </Box>

            {/* Clients Table */}
            <Paper variant="subtle" sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Beschreibung</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clients.map((client) => (
                                <TableRow 
                                    key={client.id} 
                                    hover 
                                    sx={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/app/admin/clients/${client.id}`)}
                                >
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {client.icon ? (
                                                <Avatar
                                                    src={`data:${client.icon.contentType};base64,${client.icon.data}`}
                                                    sx={{ width: 32, height: 32 }}
                                                />
                                            ) : (
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.hover' }}>
                                                    <ImageIcon sx={{ fontSize: 18 }} color="disabled" />
                                                </Avatar>
                                            )}
                                            <Typography variant="subtitle2" fontWeight="medium">
                                                {client.name}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{client.description}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {clients.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                            Keine Clients gefunden. Erstellen Sie Ihren ersten Client.
                        </Typography>
                    </Box>
                )}
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingClient ? 'Client bearbeiten' : 'Neuen Client erstellen'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            error={!!errors.name}
                            helperText={errors.name}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="Beschreibung"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            error={!!errors.description}
                            helperText={errors.description}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                        />
                        
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isPrivate ?? false}
                                        onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                                    />
                                }
                                label="Privat"
                            />
                            <Typography variant="caption" color="text.secondary">
                                Wenn aktiviert, wird diese Client-Anwendung als privat markiert.
                            </Typography>
                        </Box>
                        
                        <TextField
                            fullWidth
                            label="Auth Callback URL (optional)"
                            value={formData.authCallbackUrl ?? ''}
                            onChange={(e) => {
                                const callbackUrl = e.target.value;
                                const fqdn = extractFQDN(callbackUrl);
                                setFormData({ 
                                    ...formData, 
                                    authCallbackUrl: callbackUrl,
                                    url: fqdn || formData.url
                                });
                            }}
                            error={!!errors.authCallbackUrl}
                            helperText={errors.authCallbackUrl || 'Ohne Callback-URL ist OAuth-Login nicht möglich'}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            fullWidth
                            label="URL (optional)"
                            value={formData.url || ''}
                            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                            helperText="Haupt-URL der Anwendung"
                            sx={{ mb: 2 }}
                        />

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Icon (optional)
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                Max. 100KB, JPEG, PNG oder SVG
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                {(editingClient?.icon && !removeIcon && !iconFile) ? (
                                    <Avatar
                                        src={`data:${editingClient.icon.contentType};base64,${editingClient.icon.data}`}
                                        sx={{ width: 56, height: 56 }}
                                    />
                                ) : iconFile ? (
                                    <Avatar
                                        src={URL.createObjectURL(iconFile)}
                                        sx={{ width: 56, height: 56 }}
                                    />
                                ) : (
                                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'action.hover' }}>
                                        <ImageIcon color="disabled" />
                                    </Avatar>
                                )}
                                <Box>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        size="small"
                                        disabled={submitting}
                                    >
                                        {iconFile ? 'Anderes wählen' : 'Datei wählen'}
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/jpeg,image/jpg,image/png,image/svg+xml"
                                            onChange={handleIconFileChange}
                                        />
                                    </Button>
                                    {editingClient?.icon && !iconFile && !removeIcon && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            sx={{ ml: 1 }}
                                            onClick={() => setRemoveIcon(true)}
                                            disabled={submitting}
                                        >
                                            Entfernen
                                        </Button>
                                    )}
                                    {removeIcon && (
                                        <Button
                                            variant="text"
                                            size="small"
                                            sx={{ ml: 1 }}
                                            onClick={() => setRemoveIcon(false)}
                                            disabled={submitting}
                                        >
                                            Zurücksetzen
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                            {iconError && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {iconError}
                                </Typography>
                            )}
                        </Box>
                        
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="required-roles-label">Erforderliche Rollen (optional)</InputLabel>
                            <Select
                                labelId="required-roles-label"
                                multiple
                                value={formData.requiredRoleIds || []}
                                onChange={(e: any) => setFormData({ ...formData, requiredRoleIds: e.target.value as string[] })}
                                input={<OutlinedInput label="Erforderliche Rollen (optional)" />}
                                renderValue={(selected: any) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value: any) => {
                                            const role = availableRoles.find(r => r.id === value);
                                            return (
                                                <Chip 
                                                    key={value} 
                                                    label={role?.name || value} 
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                />
                                            );
                                        })}
                                    </Box>
                                )}
                            >
                                {availableRoles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Typography variant="body2" fontWeight="medium">
                                                {role.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {role.ident}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                            Systemrollen am Account. Der Benutzer muss mindestens eine davon besitzen, um sich bei dieser Anwendung anmelden zu können.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={submitting}>Abbrechen</Button>
                    <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                        {submitting ? 'Wird gespeichert...' : editingClient ? 'Aktualisieren' : 'Erstellen'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};
